import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  MessageSquare, Send, Trash2, Eye, ArrowLeft, Clock, User, Mail,
  CheckCircle, Circle,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function getToken() {
  return localStorage.getItem('ledgerpro_token') || localStorage.getItem('token');
}

function fmtTime(d) {
  if (!d) return '';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function SuperAdminChat() {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const token = getToken();
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const loadSessions = async () => {
    try {
      const res = await fetch(`${API_BASE}/platform/chat`, { headers });
      const data = await res.json();
      setSessions(Array.isArray(data) ? data : []);
    } catch (e) {
      // Fallback: try to load from email_log
      try {
        const res2 = await fetch(`${API_BASE}/email/log`, { headers });
        const data2 = await res2.json();
        const chatLogs = (Array.isArray(data2) ? data2 : []).filter(l => l.subject?.includes('LiveChat'));
        const sessionMap = {};
        chatLogs.forEach(log => {
          const match = log.subject?.match(/Session: (.+)/);
          const sessionId = match ? match[1] : 'unknown';
          if (!sessionMap[sessionId]) {
            sessionMap[sessionId] = {
              session_id: sessionId,
              visitor_name: 'Anonymous',
              visitor_email: log.recipient_email || '',
              message_count: 0,
              unread_count: 1,
              last_message_at: log.created_at,
              started_at: log.created_at,
            };
          }
          sessionMap[sessionId].message_count++;
        });
        setSessions(Object.values(sessionMap).sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at)));
      } catch (e2) { /* ignore */ }
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (sessionId) => {
    try {
      const res = await fetch(`${API_BASE}/platform/chat/${sessionId}`, { headers });
      const data = await res.json();
      setMessages(Array.isArray(data) ? data : []);
      // Mark as read
      await fetch(`${API_BASE}/platform/chat/read/${sessionId}`, { method: 'PUT', headers });
      // Update unread count locally
      setSessions(prev => prev.map(s => s.session_id === sessionId ? { ...s, unread_count: 0 } : s));
    } catch (e) {
      setMessages([]);
    }
  };

  useEffect(() => { loadSessions(); }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSelectSession = (session) => {
    setSelectedSession(session);
    loadMessages(session.session_id);
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!reply.trim() || !selectedSession) return;
    setSending(true);
    try {
      await fetch(`${API_BASE}/platform/chat/reply`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ sessionId: selectedSession.session_id, message: reply.trim() }),
      });
      setReply('');
      loadMessages(selectedSession.session_id);
      toast.success('Reply sent');
    } catch (e) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (sessionId) => {
    if (!confirm('Delete this entire conversation?')) return;
    try {
      await fetch(`${API_BASE}/platform/chat/${sessionId}`, { method: 'DELETE', headers });
      setSessions(prev => prev.filter(s => s.session_id !== sessionId));
      if (selectedSession?.session_id === sessionId) {
        setSelectedSession(null);
        setMessages([]);
      }
      toast.success('Conversation deleted');
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const totalUnread = sessions.reduce((sum, s) => sum + (s.unread_count || 0), 0);

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 60px)', background: '#f1f5f9' }}>
      {/* Sidebar — Session List */}
      <div style={{
        width: selectedSession ? 340 : '100%',
        maxWidth: 420,
        background: '#fff',
        borderRight: '1px solid #e2e8f0',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        transition: 'width 0.2s',
      }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', background: '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>
                <MessageSquare size={18} style={{ verticalAlign: '-3px', marginRight: 8 }} />
                Live Chat
              </h2>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: '#94a3b8' }}>
                {sessions.length} conversations {totalUnread > 0 && <span style={{ color: '#ef4444', fontWeight: 700 }}>· {totalUnread} unread</span>}
              </p>
            </div>
            <button onClick={loadSessions} style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#475569' }}>
              Refresh
            </button>
          </div>
        </div>

        {/* Session List */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading...</div>
          ) : sessions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <MessageSquare size={40} style={{ color: '#e2e8f0', marginBottom: 12 }} />
              <p style={{ color: '#94a3b8', fontSize: 14, margin: 0 }}>No conversations yet</p>
              <p style={{ color: '#cbd5e1', fontSize: 12, margin: '4px 0 0' }}>Messages from the live chat will appear here</p>
            </div>
          ) : (
            sessions.map(session => (
              <div
                key={session.session_id}
                onClick={() => handleSelectSession(session)}
                style={{
                  padding: '14px 20px',
                  borderBottom: '1px solid #f1f5f9',
                  cursor: 'pointer',
                  background: selectedSession?.session_id === session.session_id ? '#eff6ff' : '#fff',
                  borderLeft: session.unread_count > 0 ? '3px solid #2563eb' : '3px solid transparent',
                  transition: 'background 0.15s',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#2563eb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                        {session.visitor_name?.charAt(0)?.toUpperCase() || 'V'}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: session.unread_count > 0 ? 700 : 500, fontSize: 13, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {session.visitor_name || 'Anonymous Visitor'}
                        </div>
                      </div>
                      {session.unread_count > 0 && (
                        <span style={{ background: '#2563eb', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 10, flexShrink: 0 }}>
                          {session.unread_count}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8', display: 'flex', justifyContent: 'space-between' }}>
                      <span>{session.message_count} messages</span>
                      <span>{fmtTime(session.last_message_at)}</span>
                    </div>
                    {session.visitor_email && (
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Mail size={10} /> {session.visitor_email}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(session.session_id); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#94a3b8', flexShrink: 0 }}
                    title="Delete conversation"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main — Chat View */}
      {selectedSession ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#f8fafc' }}>
          {/* Chat Header */}
          <div style={{ padding: '12px 20px', background: '#fff', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => setSelectedSession(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 4 }}>
              <ArrowLeft size={18} />
            </button>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#2563eb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700 }}>
              {selectedSession.visitor_name?.charAt(0)?.toUpperCase() || 'V'}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{selectedSession.visitor_name || 'Anonymous Visitor'}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>
                {selectedSession.visitor_email || 'No email'} · Started {fmtTime(selectedSession.started_at)}
              </div>
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {messages.map((msg, i) => (
              <div key={msg.id || i} style={{ display: 'flex', justifyContent: msg.sender_type === 'agent' ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  maxWidth: '70%',
                  padding: '10px 14px',
                  borderRadius: 12,
                  background: msg.sender_type === 'agent' ? '#2563eb' : '#fff',
                  color: msg.sender_type === 'agent' ? '#fff' : '#1e293b',
                  border: msg.sender_type === 'agent' ? 'none' : '1px solid #e2e8f0',
                  borderBottomLeftRadius: msg.sender_type === 'user' ? 4 : 12,
                  borderBottomRightRadius: msg.sender_type === 'agent' ? 4 : 12,
                }}>
                  <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-line' }}>{msg.message}</div>
                  <div style={{ fontSize: 10, marginTop: 4, opacity: 0.6, textAlign: msg.sender_type === 'agent' ? 'right' : 'left' }}>
                    {msg.sender_name || (msg.sender_type === 'agent' ? 'Support' : 'Visitor')} · {fmtTime(msg.created_at)}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Reply Input */}
          <form onSubmit={handleReply} style={{ padding: '12px 20px', background: '#fff', borderTop: '1px solid #e2e8f0', display: 'flex', gap: 8 }}>
            <input
              value={reply}
              onChange={e => setReply(e.target.value)}
              placeholder="Type your reply..."
              style={{ flex: 1, padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit' }}
            />
            <button
              type="submit"
              disabled={!reply.trim() || sending}
              style={{ padding: '10px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, opacity: (!reply.trim() || sending) ? 0.5 : 1 }}
            >
              <Send size={14} /> Send
            </button>
          </form>
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
          <div style={{ textAlign: 'center' }}>
            <MessageSquare size={48} style={{ color: '#e2e8f0', marginBottom: 12 }} />
            <p style={{ fontSize: 16, fontWeight: 600, color: '#64748b', margin: 0 }}>Select a conversation</p>
            <p style={{ fontSize: 13, color: '#94a3b8', margin: '4px 0 0' }}>Click a chat from the sidebar to view and reply</p>
          </div>
        </div>
      )}
    </div>
  );
}
